import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { nextStatus, SHORT_STATUS } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

// One-tap "move to next stage" used by the board, kiosk and scan flows.
export default function AdvanceButton({
  rework,
  onDone,
  size = "default",
  testid,
  className = "",
}) {
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(false);
  const [destination, setDestination] = useState(null);
  const { user } = useAuth();
  const canAdvance = ["admin", "lead", "kiosk"].includes(user?.role);
  const nxt = nextStatus(rework.status);
  if (!nxt || !canAdvance) return null;

  const advance = async (completion, bayNumber) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reworks/${rework.id}/advance`, {
        completion,
        bay_number: bayNumber,
      });
      toast.success(`${rework.job_number} → ${SHORT_STATUS[data.status] || data.status}`);
      setPick(false);
      setDestination(null);
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not advance");
    } finally {
      setBusy(false);
    }
  };

  const big = size === "lg";
  const bays = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  return (
    <>
      <Button disabled={busy} data-testid={testid || `advance-${rework.id}`}
        onClick={() => {
          if (nxt === "Complete") {
            setDestination("Finished Rack");
            setPick(true);
            return;
          }
          advance();
        }}
        className={`bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] rounded-sm uppercase tracking-widest ${
          big ? "h-14 px-6 text-sm w-full" : "h-8 px-3 text-[11px]"
        } ${className}`}
      >
        {nxt === "Complete" ? "Mark complete" : `→ ${nxt}`}
        {!big && <ArrowRight size={12} className="ml-1.5" />}
      </Button>
      <Dialog
        open={pick}
        onOpenChange={(open) => {
          setPick(open);
          if (!open) setDestination(null);
        }}
      >
        <DialogContent className="bg-white border-[#e6e5df] rounded-sm" data-testid="complete-dialog">
          {(
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-[#232826]">
                  Select Finished Rack bay
                </DialogTitle>
                <DialogDescription className="text-[#6b6a68]">
                  Select the bay (1–10) where this completed part has been placed. Leads can move it to Maintenance Clearing afterwards.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 pt-2 sm:grid-cols-5">
                {bays.map((bay) => (
                  <button
                    key={bay}
                    disabled={busy}
                    onClick={() => advance(destination, bay)}
                    data-testid={`complete-bay-${bay}`}
                    className={[
                      "min-h-16 rounded-sm border border-[#647265] bg-[#efece5] text-lg",
                      "font-bold text-[#232826] transition-colors hover:bg-[#647265]",
                      "hover:text-[#f7f5f1]",
                    ].join(" ")}
                  >
                    Bay {bay}
                  </button>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
